import type { FormField } from '../interfaces/form.interface';
import { DropdownData, InputType } from '../interfaces/input.interface';

export type ReactiveDropdownOption = {
    label?: string;
    value: unknown;
};

export type ReactiveDropdownLoadState = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

const NULLISH_STRING_TOKENS: ReadonlySet<string> = new Set([
    '',
    'null',
    'undefined',
    'nan',
    'false',
]);

const BAD_DISPLAY_TOKENS: ReadonlySet<string> = new Set([
    ...NULLISH_STRING_TOKENS,
    'invalid date',
]);

export class ReactiveFormValueUtils {
    public static normalizeToken(value: string): string {
        return value.trim();
    }

    public static isNullishStringToken(value: string): boolean {
        return NULLISH_STRING_TOKENS.has(value.trim().toLowerCase());
    }

    public static isBadDisplayToken(value: string): boolean {
        return BAD_DISPLAY_TOKENS.has(value.trim().toLowerCase());
    }

    public static hasNonEmptyValue(value: unknown): boolean {
        if (value === null || value === undefined) {
            return false;
        }

        if (typeof value === 'string') {
            const normalized = value.trim().toLowerCase();

            return normalized !== '' && !ReactiveFormValueUtils.isNullishStringToken(normalized);
        }

        if (Array.isArray(value)) {
            return value.length > 0;
        }

        return true;
    }

    public static splitSerializedIds(value: string): string[] {
        return value
            .split(/[;,]/)
            .map((item) => ReactiveFormValueUtils.normalizeToken(item))
            .filter((item) => item.length > 0 && !ReactiveFormValueUtils.isBadDisplayToken(item));
    }

    public static isRepeatableField<T>(field: FormField<T>): boolean {
        return field.input?.type === InputType.REPEATABLE_GROUP;
    }

    public static isTwoFieldsConcatField<T>(field: FormField<T>): boolean {
        return field.input?.type === InputType.TWO_FIELDS_CONCAT;
    }

    public static isMultiSelectField<T>(field: FormField<T>): boolean {
        return field.input?.type === InputType.MULTISELECT || field.input?.type === InputType.MULTISELECT_SEARCH;
    }

    public static isSingleDropdownField<T>(field: FormField<T>): boolean {
        return !!field.input?.dropdownConfiguration?.valuePrimaryKey && !ReactiveFormValueUtils.isMultiSelectField(field);
    }

    public static getFieldPrimaryKey<T>(field: FormField<T>): string {
        return field.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
    }

    public static getDropdownOptions(dropdownData?: DropdownData<unknown>): ReactiveDropdownOption[] {
        const filtered = dropdownData?.filtered as ReactiveDropdownOption[] | undefined;

        if (Array.isArray(filtered) && filtered.length > 0) {
            return filtered;
        }

        const values = dropdownData?.values as ReactiveDropdownOption[] | undefined;

        return Array.isArray(values) ? values : [];
    }

    public static isFieldReady<T>(field: FormField<T>, formType: number, dropdownsFinalized: boolean): boolean {
        const loadState = ReactiveFormValueUtils.getDropdownLoadState(field, formType, dropdownsFinalized);

        return loadState === 'ready' || loadState === 'empty' || loadState === 'idle';
    }

    public static getDropdownLoadState<T>(
        field: FormField<T>,
        formType: number,
        dropdownsFinalized: boolean,
    ): ReactiveDropdownLoadState {
        const isReadOnlyForm = formType >= 3 && formType !== 6;

        if (isReadOnlyForm || field.input?.isEditable === false) {
            return 'ready';
        }

        const dropdownData = field?.input?.data as DropdownData<unknown> | undefined;

        if (!ReactiveFormValueUtils.isDropdownLikeField(field, dropdownData)) {
            return 'ready';
        }

        if (ReactiveFormValueUtils.hasRealDropdownOptions(dropdownData)) {
            return 'ready';
        }

        if (field.dataSource?.lazy === true && !dropdownsFinalized) {
            return 'idle';
        }

        return dropdownsFinalized ? 'empty' : 'loading';
    }

    public static isDropdownLikeField<T>(field: FormField<T>, dropdownData?: DropdownData<unknown>): boolean {
        return Boolean(
            field.input?.dropdownConfiguration?.valuePrimaryKey ||
            Array.isArray(dropdownData?.values) ||
            Array.isArray(dropdownData?.filtered),
        );
    }

    public static hasRealDropdownOptions(dropdownData?: DropdownData<unknown>): boolean {
        const options = ReactiveFormValueUtils.getDropdownOptions(dropdownData);

        return options.some((item) => {
            const label = String(item?.label ?? '').trim();

            return label.length > 0 && label !== '...' && item?.value != null;
        });
    }

    public static createOptionsIndex(
        options: ReadonlyArray<ReactiveDropdownOption>,
        primaryKey: string,
    ): Map<string, ReactiveDropdownOption> {
        const index = new Map<string, ReactiveDropdownOption>();

        for (const option of options) {
            const comparableId = ReactiveFormValueUtils.extractComparableId(option.value, primaryKey);

            if (comparableId !== null) {
                index.set(String(comparableId), option);
            }
        }

        return index;
    }

    public static mapIdToOptionValue(
        id: string | number,
        options: ReadonlyArray<ReactiveDropdownOption>,
        primaryKey: string,
        optionsIndex?: ReadonlyMap<string, ReactiveDropdownOption>,
    ): unknown | null {
        const indexedOption = optionsIndex?.get(String(id));

        if (indexedOption) {
            return indexedOption.value;
        }

        const found = options.find((option) => {
            const raw = option && typeof option === 'object' ? option.value : option;

            if (raw === null || raw === undefined) {
                return false;
            }

            if (typeof raw !== 'object') {
                return String(raw) === String(id);
            }

            return String((raw as Record<string, unknown>)[primaryKey]) === String(id);
        });

        return found ? found.value : null;
    }

    public static extractComparableId(value: unknown, primaryKey: string): string | number | null {
        if (typeof value === 'number') {
            return value;
        }

        if (typeof value === 'string') {
            return ReactiveFormValueUtils.isBadDisplayToken(value) ? null : value;
        }

        if (typeof value === 'object' && value !== null) {
            const record = value as Record<string, unknown>;
            const fromPrimaryKey = record[primaryKey];
            const fromId = record['id'];

            if (typeof fromPrimaryKey === 'number') {
                return fromPrimaryKey;
            }

            if (typeof fromPrimaryKey === 'string' && !ReactiveFormValueUtils.isBadDisplayToken(fromPrimaryKey)) {
                return fromPrimaryKey;
            }

            if (typeof fromId === 'number') {
                return fromId;
            }

            if (typeof fromId === 'string' && !ReactiveFormValueUtils.isBadDisplayToken(fromId)) {
                return fromId;
            }
        }

        return null;
    }

    public static remapValueWithOptions(
        value: unknown,
        options: ReactiveDropdownOption[],
        primaryKey: string,
        isMulti: boolean,
        dropdownsFinalized: boolean,
        optionsIndex?: ReadonlyMap<string, ReactiveDropdownOption>,
    ): unknown {
        if (isMulti) {
            return ReactiveFormValueUtils.remapMultiValueWithOptions(
                value,
                options,
                primaryKey,
                dropdownsFinalized,
                optionsIndex,
            );
        }

        return ReactiveFormValueUtils.remapSingleValueWithOptions(
            value,
            options,
            primaryKey,
            dropdownsFinalized,
            optionsIndex,
        );
    }

    public static areValuesEquivalent(first: unknown, second: unknown): boolean {
        if (first === second) {
            return true;
        }

        if (Array.isArray(first) && Array.isArray(second)) {
            if (first.length !== second.length) {
                return false;
            }

            for (let index = 0; index < first.length; index += 1) {
                if (String(first[index]) !== String(second[index])) {
                    return false;
                }
            }

            return true;
        }

        return String(first ?? '') === String(second ?? '');
    }

    public static areModelSnapshotsEquivalent(first: Record<string, unknown>, second: Record<string, unknown>): boolean {
        const firstKeys = Object.keys(first ?? {});
        const secondKeys = Object.keys(second ?? {});

        if (firstKeys.length !== secondKeys.length) {
            return false;
        }

        for (const key of firstKeys) {
            if (!ReactiveFormValueUtils.areValuesEquivalent(first[key], second[key])) {
                return false;
            }
        }

        return true;
    }

    public static formatPreviewValue<T>(value: unknown, field: FormField<T>): string {
        if (Array.isArray(value)) {
            const parts = value
                .map((item) => ReactiveFormValueUtils.toDisplayText(item, field))
                .filter((item) => item.length > 0);

            if (parts.length === 0) {
                return '';
            }

            const visibleParts = parts.slice(0, 3);
            const hiddenItems = parts.length - visibleParts.length;

            return hiddenItems > 0 ? `${visibleParts.join(', ')} +${hiddenItems}` : visibleParts.join(', ');
        }

        return ReactiveFormValueUtils.toDisplayText(value, field);
    }

    public static splitTwoFieldsConcat(value: string, separator: string): [string, string] {
        if (!value) {
            return ['', ''];
        }

        const normalizedSeparator = (separator ?? '').trim();

        if (!normalizedSeparator) {
            return [value, ''];
        }

        const separatorIndex = value.indexOf(normalizedSeparator);

        if (separatorIndex < 0) {
            return [value, ''];
        }

        const leftValue = value.slice(0, separatorIndex).trim();
        const rightValue = value.slice(separatorIndex + normalizedSeparator.length).trim();

        return [leftValue, rightValue];
    }

    private static remapMultiValueWithOptions(
        value: unknown,
        options: ReactiveDropdownOption[],
        primaryKey: string,
        dropdownsFinalized: boolean,
        optionsIndex?: ReadonlyMap<string, ReactiveDropdownOption>,
    ): unknown {
        const values = Array.isArray(value) ? value : [];
        const ids = values
            .map((item) => ReactiveFormValueUtils.extractComparableId(item, primaryKey))
            .filter((item): item is string | number => item !== null);
        const mappedValues = ids
            .map((id) => ReactiveFormValueUtils.mapIdToOptionValue(id, options, primaryKey, optionsIndex))
            .filter((item) => item !== null);

        if (mappedValues.length > 0) {
            return mappedValues;
        }

        if (dropdownsFinalized) {
            return [];
        }

        return ids.length > 0 ? ids : values;
    }

    private static remapSingleValueWithOptions(
        value: unknown,
        options: ReactiveDropdownOption[],
        primaryKey: string,
        dropdownsFinalized: boolean,
        optionsIndex?: ReadonlyMap<string, ReactiveDropdownOption>,
    ): unknown {
        if (value === null || value === undefined) {
            return null;
        }

        if (typeof value === 'string' && ReactiveFormValueUtils.isBadDisplayToken(value)) {
            return null;
        }

        const id = ReactiveFormValueUtils.extractComparableId(value, primaryKey);
        const mappedValue = id === null
            ? null
            : ReactiveFormValueUtils.mapIdToOptionValue(id, options, primaryKey, optionsIndex);

        if (mappedValue !== null) {
            return mappedValue;
        }

        if (dropdownsFinalized) {
            return null;
        }

        return value;
    }

    private static toDisplayText<T>(value: unknown, field: FormField<T>): string {
        if (value === null || value === undefined) {
            return '';
        }

        if (typeof value === 'string') {
            return ReactiveFormValueUtils.isBadDisplayToken(value) ? '' : value;
        }

        if (typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }

        if (typeof value === 'object') {
            const primaryKey = ReactiveFormValueUtils.getFieldPrimaryKey(field);
            const record = value as Record<string, unknown>;
            const candidates: unknown[] = [
                record['label'],
                record['nombre'],
                record['name'],
                record['razon_social'],
                record['descripcion'],
                record[primaryKey],
                record['id'],
            ];

            for (const candidate of candidates) {
                if (typeof candidate === 'string' && !ReactiveFormValueUtils.isBadDisplayToken(candidate) && candidate.trim().length > 0) {
                    return candidate.trim();
                }

                if (typeof candidate === 'number') {
                    return String(candidate);
                }
            }
        }

        return '';
    }
}
